import { BALANCE } from '../config/balance';
import { PALETTE } from '../assets/palette';
import { sprite, type Sprite } from './sprites';

type Ctx = CanvasRenderingContext2D;

function circle(ctx: Ctx, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
}

function diamond(ctx: Ctx, r: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.lineTo(r, 0);
  ctx.lineTo(0, r);
  ctx.lineTo(-r, 0);
  ctx.closePath();
  ctx.fill();
}

export function chaserSprite(flash: boolean): Sprite {
  const c = BALANCE.enemies.chaser;
  return sprite(flash ? 'chaser-f' : 'chaser', c.radius + 1, (x) =>
    circle(x, c.radius, flash ? PALETTE.flash : PALETTE.chaser));
}

export function spitterSprite(flash: boolean): Sprite {
  const c = BALANCE.enemies.spitter;
  return sprite(flash ? 'spitter-f' : 'spitter', c.radius + 1, (x) => {
    diamond(x, c.radius, flash ? PALETTE.flash : PALETTE.spitter);
    if (!flash) circle(x, 2.6, PALETTE.warn);
  });
}

export function eliteSprite(flash: boolean): Sprite {
  const c = BALANCE.enemies.elite;
  return sprite(flash ? 'elite-f' : 'elite', c.radius + 3, (x) => {
    x.fillStyle = flash ? PALETTE.flash : PALETTE.ink;
    x.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const px = Math.cos(a) * c.radius;
      const py = Math.sin(a) * c.radius;
      if (i === 0) x.moveTo(px, py);
      else x.lineTo(px, py);
    }
    x.closePath();
    x.fill();
    x.strokeStyle = PALETTE.terracotta;
    x.lineWidth = 2;
    x.stroke();
  });
}

/** Boss 主体（不含旋转刻度，刻度实时画）。 */
export function bossBodySprite(kind: 'boss1' | 'boss2', flash: boolean): Sprite {
  const c = kind === 'boss1' ? BALANCE.enemies.boss1 : BALANCE.enemies.boss2;
  return sprite(flash ? `${kind}-f` : kind, c.radius + 4, (x) => {
    circle(x, c.radius, flash ? PALETTE.flash : PALETTE.ink);
    x.strokeStyle = PALETTE.terracotta;
    x.lineWidth = 3;
    x.beginPath();
    x.arc(0, 0, c.radius * 0.62, 0, Math.PI * 2);
    x.stroke();
  });
}

/** 冲锋犬：朝 +x 方向，颜色随状态。 */
export function chargerSprite(color: string, key: string): Sprite {
  const r = BALANCE.enemies.charger.radius + 2;
  return sprite(key, r + 1, (x) => {
    x.fillStyle = color;
    x.beginPath();
    x.moveTo(r, 0);
    x.lineTo(0, r * 0.8);
    x.lineTo(-r * 0.55, 0);
    x.lineTo(0, -r * 0.8);
    x.closePath();
    x.fill();
  });
}

/** 自爆菇主体（不含引信环，环实时画）。blink=引信闪烁。 */
export function bomberSprite(blink: boolean): Sprite {
  const c = BALANCE.enemies.bomber;
  return sprite(blink ? 'bomber-b' : 'bomber', c.radius + 2, (x) => {
    x.fillStyle = blink ? PALETTE.warn : PALETTE.bomberStem;
    x.fillRect(-3.5, -2, 7, c.radius);
    x.fillStyle = blink ? PALETTE.flash : PALETTE.bomberCap;
    x.beginPath();
    x.arc(0, -2, c.radius, Math.PI, 0);
    x.fill();
  });
}

export function playerSprite(): Sprite {
  const r = BALANCE.player.radius;
  return sprite('player', r + 1, (x) => {
    circle(x, r, PALETTE.ink);
    x.strokeStyle = PALETTE.ivory;
    x.lineWidth = 2;
    x.beginPath();
    x.arc(0, 0, r * 0.62, 0, Math.PI * 2);
    x.stroke();
    circle(x, 4.5, PALETTE.terracotta);
  });
}

export function staffBoltSprite(): Sprite {
  return sprite('staff-bolt', 9, (x) => {
    circle(x, BALANCE.weapons.staff.size, PALETTE.ink);
    circle(x, 2.4, PALETTE.ivory);
  });
}

export function arrowBoltSprite(): Sprite {
  const s = BALANCE.weapons.arrow.size;
  return sprite('arrow-bolt', s * 1.8, (x) => {
    x.fillStyle = PALETTE.terracotta;
    x.beginPath();
    x.moveTo(s * 1.6, 0);
    x.lineTo(-s, s * 0.8);
    x.lineTo(-s, -s * 0.8);
    x.closePath();
    x.fill();
  });
}

export function foeBoltSprite(): Sprite {
  const c = BALANCE.enemies.spitter;
  return sprite('foe-bolt', c.boltRadius + 1, (x) =>
    circle(x, c.boltRadius, PALETTE.warn));
}

export function gemSprite(core: boolean): Sprite {
  if (core) {
    return sprite('gem-core', 10, (x) => {
      diamond(x, 8, PALETTE.terracotta);
      x.strokeStyle = PALETTE.ink;
      x.lineWidth = 1.5;
      x.beginPath();
      x.moveTo(0, -8);
      x.lineTo(8, 0);
      x.lineTo(0, 8);
      x.lineTo(-8, 0);
      x.closePath();
      x.stroke();
    });
  }
  return sprite('gem', 6, (x) => {
    diamond(x, 4.5, PALETTE.gem);
    x.fillStyle = PALETTE.flash;
    x.fillRect(-2.4, -2.4, 1.4, 1.4);
  });
}

export function coinSprite(): Sprite {
  return sprite('coin', 6, (x) => {
    circle(x, 4.5, PALETTE.terracotta);
    circle(x, 1.8, PALETTE.ivory);
  });
}

export function heartSprite(): Sprite {
  return sprite('heart', 8, (x) => {
    x.fillStyle = PALETTE.heal;
    x.beginPath();
    x.moveTo(0, 5.5);
    x.bezierCurveTo(-8, -1, -4.5, -6.5, 0, -2.5);
    x.bezierCurveTo(4.5, -6.5, 8, -1, 0, 5.5);
    x.closePath();
    x.fill();
  });
}
